import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Download, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { SiteNav } from "@/components/SiteChrome";

const Pill = ({ on, children, ...p }) => (
  <button type="button" {...p} className={`rounded-full border-2 px-4 py-2 text-sm text-left transition-colors ${on ? "border-pinkb bg-pinkb/5 text-navy" : "border-border bg-white hover:border-pinkb/50"}`}>
    {on && <Check className="inline h-3.5 w-3.5 mr-1 text-pinkb" />}{children}
  </button>
);

const Field = ({ f, v, set }) => {
  if (f.kind === "subheading") return <div className="pt-4"><h3 className="font-serif uppercase text-xl text-navy">{f.label}</h3>{f.help && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{f.help}</p>}</div>;
  const id = `pq-${f.id}`;
  return (
    <div className="space-y-2" data-testid={`pq-field-${f.id}`}>
      <label htmlFor={id} className="block font-medium text-navy">{f.label}{f.required && <span className="text-pinkb"> *</span>}</label>
      {f.help && <p className="text-xs text-muted-foreground whitespace-pre-line">{f.help}</p>}
      {f.type === "textarea" ? <Textarea id={id} rows={4} value={v || ""} onChange={(e) => set(e.target.value)} data-testid={id} className="bg-white" />
        : f.type === "choice" ? <div className="flex flex-wrap gap-2">{f.options.map((o) => <Pill key={o} on={v === o} onClick={() => set(v === o ? "" : o)} data-testid={`${id}-opt-${f.options.indexOf(o)}`}>{o}</Pill>)}</div>
        : f.type === "multi" ? <div className="flex flex-wrap gap-2">{f.options.map((o) => { const on = (v || []).includes(o); return <Pill key={o} on={on} onClick={() => set(on ? v.filter((x) => x !== o) : [...(v || []), o])} data-testid={`${id}-opt-${f.options.indexOf(o)}`}>{o}</Pill>; })}</div>
        : <Input id={id} type={f.type === "email" ? "email" : "text"} value={v || ""} onChange={(e) => set(e.target.value)} data-testid={id} className="bg-white" />}
    </div>
  );
};

const Done = ({ outro, file }) => (
  <div className="rounded-3xl bg-navy text-white p-8 lg:p-12 fade-up" data-testid="pq-done">
    {file ? (
      <>
        <p className="font-serif uppercase text-2xl text-sun mb-4">Your Mini Marketing Plan is ready.</p>
        <p className="text-white/90">Start with the next 3 actions at the end. Any questions, email lindsay@rootsmarketingco.uk.</p>
        <a href={file} data-testid="pq-download-plan" className="mt-6 inline-flex items-center gap-2 rounded-full bg-pinkb hover:bg-pinkb/90 px-6 py-3 transition-colors"><Download className="h-4 w-4" />Download my plan (PDF)</a>
      </>
    ) : outro.split("\n").map((l, i) => <p key={i} className={i === 0 ? "font-serif uppercase text-2xl text-sun mb-4" : "mt-3 text-white/90"}>{l}</p>)}
  </div>
);

export default function PlanQuestionnaire() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [a, setA] = useState({});
  const [step, setStep] = useState(-1);
  const [saved, setSaved] = useState("");
  const [busy, setBusy] = useState(false);
  const timer = useRef();
  const pending = useRef(null);
  const flush = () => {
    if (!pending.current) return;
    clearTimeout(timer.current);
    const body = pending.current;
    pending.current = null;
    api.put(`/plan-questionnaire/${token}`, { answers: body }).then(() => setSaved("Saved")).catch(() => setSaved("Not saved, check your connection"));
  };
  useEffect(() => {
    const onLeave = () => {
      if (!pending.current) return;
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/plan-questionnaire/${token}`, { method: "PUT", keepalive: true, headers: { "Content-Type": "application/json" }, body: JSON.stringify({ answers: pending.current }) });
    };
    window.addEventListener("beforeunload", onLeave);
    window.addEventListener("pagehide", onLeave);
    return () => { window.removeEventListener("beforeunload", onLeave); window.removeEventListener("pagehide", onLeave); };
  }, [token]);
  useEffect(() => {
    api.get(`/plan-questionnaire/${token}`).then((r) => { setD(r.data); setA(r.data.answers || {}); }).catch((e) => setErr(errMsg(e)));
  }, [token]);
  const secs = d ? d.form.sections.filter((s) => !s.show_if || a[s.show_if.field] === s.show_if.equals) : [];
  const set = (id, v) => {
    const next = { ...a, [id]: v };
    setA(next);
    pending.current = next;
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 1200);
    setSaved("Saving…");
  };
  const go = (n) => { flush(); setStep(n); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const gaps = (s) => s.fields.filter((f) => f.required && !(Array.isArray(a[f.id]) ? a[f.id].length : a[f.id]));
  const next = () => {
    const g = gaps(secs[step]);
    if (g.length) return toast.error(`Please answer: ${g[0].label}`);
    go(step + 1);
  };
  const submit = async () => {
    const g = gaps(secs[step]);
    if (g.length) return toast.error(`Please answer: ${g[0].label}`);
    setBusy(true);
    clearTimeout(timer.current);
    pending.current = null;
    try { const r = await api.post(`/plan-questionnaire/${token}/submit`, { answers: a }); setD({ ...d, submitted_at: r.data.submitted_at }); window.scrollTo({ top: 0 }); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const s = secs[step];
  return (
    <div className="relative z-10 min-h-screen bg-background"><SiteNav />
      <div className="max-w-3xl mx-auto px-6 py-12" data-testid="plan-questionnaire">
        {err && <p className="rounded-xl bg-rose-50 border border-rose-200 p-4" data-testid="pq-error">{err}. Please use the link from your confirmation email, or contact lindsay@rootsmarketingco.uk.</p>}
        {d && (d.submitted_at || d.has_file ? <Done outro={d.form.outro} file={d.has_file ? `${process.env.REACT_APP_BACKEND_URL}/api/plan-questionnaire/${token}/file` : ""} /> : step < 0 ? (
          <div className="fade-up" data-testid="pq-intro">
            <div className="eyebrow mb-3">Mini Marketing Plan</div>
            <h1 className="font-serif uppercase text-4xl sm:text-5xl text-navy leading-[0.95]">{d.form.title}</h1>
            <div className="mt-6 space-y-4 text-foreground/85">{d.form.intro.map((p) => <p key={p.slice(0, 20)}>{p}</p>)}</div>
            <Button onClick={() => go(0)} data-testid="pq-start" className="mt-8 rounded-full bg-pinkb hover:bg-pinkb/90 text-white px-8 py-6 text-base">{d.saved_at ? "Carry on where I left off" : "Let's get started"} <ArrowRight className="h-4 w-4 ml-2" /></Button>
          </div>
        ) : (
          <div key={step} className="fade-up">
            <div className="flex items-center justify-between text-xs font-mono text-muted-foreground mb-2"><span data-testid="pq-progress">Page {step + 1} of {secs.length}</span><span data-testid="pq-saved">{saved}</span></div>
            <div className="h-1.5 rounded-full bg-secondary overflow-hidden mb-8"><div className="h-full bg-pinkb transition-[width] duration-500" style={{ width: `${((step + 1) / secs.length) * 100}%` }} /></div>
            <h2 className="font-serif uppercase text-3xl text-navy" data-testid="pq-section-title">{s.title}</h2>
            {s.intro && <p className="mt-2 text-sm text-muted-foreground whitespace-pre-line">{s.intro}</p>}
            <div className="mt-8 space-y-7">{s.fields.map((f, i) => <Field key={f.id || `h${i}`} f={f} v={a[f.id]} set={(v) => set(f.id, v)} />)}</div>
            <div className="mt-10 flex items-center justify-between">
              <Button variant="outline" onClick={() => go(step - 1)} data-testid="pq-back" className="rounded-full"><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
              {step < secs.length - 1 ? <Button onClick={next} data-testid="pq-next" className="rounded-full bg-navy hover:bg-navy/90 text-white">Next <ArrowRight className="h-4 w-4 ml-1" /></Button>
                : <Button onClick={submit} disabled={busy} data-testid="pq-submit" className="rounded-full bg-pinkb hover:bg-pinkb/90 text-white px-6">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Send to Lindsay</Button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
